import { Building2 } from 'lucide-react';
import { api } from '../../lib/api';
import { cn } from '../../lib/utils';

/**
 * Muted tint pairs for the default tile, light and dark. Written out in full rather than composed
 * from a colour name, because Tailwind only emits classes it can read verbatim in the source.
 */
const MARK_TINTS = [
  'bg-sky-100 text-sky-700 dark:bg-sky-950/60 dark:text-sky-300',
  'bg-violet-100 text-violet-700 dark:bg-violet-950/60 dark:text-violet-300',
  'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300',
  'bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300',
  'bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300',
  'bg-teal-100 text-teal-700 dark:bg-teal-950/60 dark:text-teal-300',
  'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300',
  'bg-pink-100 text-pink-700 dark:bg-pink-950/60 dark:text-pink-300',
];

/** Stable per slug, so a project keeps its colour across reloads and between two people's screens. */
function tintFor(slug: string): string {
  let hash = 0;
  for (let i = 0; i < slug.length; i++) hash = (hash * 31 + slug.charCodeAt(i)) >>> 0;
  return MARK_TINTS[hash % MARK_TINTS.length];
}

export interface ProjectMarkSubject {
  name: string;
  slug: string;
  /** A member's chosen emoji, or null. */
  icon: string | null;
  /** When an image was uploaded as the mark, or null for none. Versions the image URL. */
  iconImageAt: string | null;
  /** Null for the organization root, which reads as a different kind of thing from a project. */
  parentId: string | null;
}

const SIZES = {
  sm: { box: 'w-4 h-4 rounded-[4px]', initial: 'text-[9px]', emoji: 'text-[13px]', glyph: 12 },
  lg: { box: 'w-10 h-10 rounded-lg', initial: 'text-base', emoji: 'text-2xl', glyph: 20 },
} as const;

/**
 * A project's mark wherever it is listed: the image a member uploaded, else the emoji they chose,
 * else a small tile carrying the project's initial, tinted per project.
 *
 * The tile replaced a folder glyph that was the same on every row. Producers navigate by client,
 * and a column of identical folders makes them read every name; a lettered, coloured tile is what
 * lets the eye find "Thrasos" among a dozen siblings without reading — and a chosen emoji or a
 * client's own logo does that better still, which is why both are offered. The server keeps image
 * and emoji exclusive, so the order here is a fallback chain rather than a ranking. The organization
 * root keeps its building icon unless given a mark.
 *
 * `active` is the orange selected-row treatment, where the tint would clash and the tile goes
 * white-on-translucent instead.
 */
export function ProjectMark({
  project, active = false, size = 'sm', className,
}: {
  project: ProjectMarkSubject;
  active?: boolean;
  size?: keyof typeof SIZES;
  className?: string;
}) {
  const dims = SIZES[size];

  if (project.iconImageAt) {
    return (
      <img
        src={api.projects.iconUrl(project.slug, project.iconImageAt)}
        alt=""
        aria-hidden
        className={cn('object-cover flex-shrink-0 bg-surface-subtle', dims.box, className)}
      />
    );
  }

  if (project.icon) {
    return (
      <span
        aria-hidden
        className={cn('flex items-center justify-center flex-shrink-0 leading-none', dims.box, dims.emoji, className)}
      >
        {project.icon}
      </span>
    );
  }

  if (project.parentId === null) {
    return (
      <Building2
        size={dims.glyph}
        className={cn('flex-shrink-0', active ? 'text-orange-100' : 'text-ink-faint', className)}
      />
    );
  }

  const initial = project.name.trim().charAt(0).toUpperCase() || '·';
  return (
    <span
      aria-hidden
      className={cn(
        'flex items-center justify-center flex-shrink-0 font-bold leading-none',
        dims.box,
        dims.initial,
        active ? 'bg-white/25 text-white' : tintFor(project.slug),
        className,
      )}
    >
      {initial}
    </span>
  );
}
